<template>
  <div class="mb-3 flex flex-column gap-3">
    <div v-for="type in runningTypes" :key="type" class="automation-card">
      <div class="automation-card-header">
        <span class="automation-dot"></span>
        <span class="automation-title flex-1">{{ typeLabel(type) }}</span>
        <Button
          icon="pi pi-pencil"
          class="p-button-sm p-button-text p-button-rounded flex-none"
          @click="openEditDialog(type)"
        />
      </div>

      <template v-if="type === 'interval'">
        <div class="flex flex-wrap align-items-center gap-4 text-sm">
          <div>
            <div class="text-color-secondary">Start</div>
            <div class="font-semibold">
              {{ formatValue(changes[type].change.start_value) }}
            </div>
          </div>
          <div>
            <div class="text-color-secondary">Current</div>
            <div class="font-semibold">
              {{ formatValue(changes[type].change.current_value) }}
            </div>
          </div>
          <div>
            <div class="text-color-secondary">Target</div>
            <div class="font-semibold">
              {{ formatValue(changes[type].change.target_value) }}
            </div>
          </div>
          <div>
            <div class="text-color-secondary">Days left</div>
            <div class="font-semibold">{{ daysLeft(type) }}</div>
          </div>
          <Button
            label="Stop"
            class="p-button-sm p-button-outlined p-button-warning ml-auto"
            :loading="loadingType === type"
            @click="confirmStop(type)"
          />
        </div>
      </template>

      <template v-else>
        <div class="flex flex-column gap-2 text-sm">
          <div
            v-for="row in geoShiftRows(changes[type].change)"
            :key="row.geo"
            class="flex align-items-center gap-3"
          >
            <span class="font-semibold" style="min-width: 4rem">{{
              countryName(row.geo)
            }}</span>
            <span
              >{{ row.start }}% → {{ row.current }}% (target
              {{ row.target }}%)</span
            >
          </div>
          <div class="flex align-items-center gap-4">
            <div>
              <span class="text-color-secondary">Days left:</span>
              {{ daysLeft(type) }}
            </div>
            <Button
              label="Stop"
              class="p-button-sm p-button-outlined p-button-warning ml-auto"
              :loading="loadingType === type"
              @click="confirmStop(type)"
            />
          </div>
        </div>
      </template>

      <div v-if="changes[type].logs.length" class="mt-3">
        <div class="text-sm font-semibold mb-1">Logs</div>
        <ul class="m-0 pl-3 text-sm text-color-secondary">
          <li v-for="log in changes[type].logs" :key="log.id">
            <template v-if="type === 'interval'">
              {{ formatDate(log.timestamp) }} — {{ log.event }}:
              {{ formatValue(log.old_value) }} →
              {{ formatValue(log.new_value) }}
            </template>
            <template v-else>
              {{ formatDate(log.timestamp) }} — {{ log.event }}
            </template>
          </li>
        </ul>
      </div>
    </div>

    <small
      v-if="props.disableIntervalAutomation"
      class="text-color-secondary block"
    >
      <i class="pi pi-info-circle mr-1"></i>
      Interval automation is disabled while this campaign uses a custom monthly
      interval schedule.
    </small>

    <Button
      v-if="!loading && addableTypeOptions.length"
      label="Add Automation"
      class="w-full mt-2"
      @click="openAddDialog"
    />

    <Dialog
      v-model:visible="showAddDialog"
      header="Add Automation"
      modal
      :style="{ width: automationType === 'geo_shift' ? '60rem' : '30rem' }"
    >
      <div class="flex flex-column gap-3">
        <div class="flex gap-3">
          <div
            :style="
              automationType === 'geo_shift'
                ? 'flex: 1 1 65%'
                : 'flex: 1 1 100%'
            "
          >
            <label>Automation type</label>
            <Dropdown
              v-model="automationType"
              :options="addableTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select automation type"
              class="w-full"
            />
          </div>
          <div v-if="automationType === 'geo_shift'" style="flex: 1 1 35%">
            <label>Over how many days</label>
            <InputNumber
              v-model="days"
              :min="2"
              showButtons
              buttonLayout="horizontal"
              decrementButtonClass="p-button-outlined"
              incrementButtonClass="p-button-outlined"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
              class="w-full"
              :inputStyle="{ width: '100%' }"
            />
          </div>
        </div>

        <template v-if="automationType === 'interval'">
          <div class="flex gap-3">
            <div style="flex: 1 1 55%">
              <label>Target value</label>
              <InputNumber
                v-model="targetValue"
                :min="0"
                :minFractionDigits="0"
                :maxFractionDigits="4"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
            </div>
            <div style="flex: 1 1 45%">
              <label>Over how many days</label>
              <InputNumber
                v-model="days"
                :min="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-outlined"
                incrementButtonClass="p-button-outlined"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
            </div>
          </div>
          <small class="text-color-secondary">
            Moves the volume to the target gradually, updating automatically
            once a day. You can stop or edit it any time.
          </small>
        </template>

        <template v-else-if="automationType === 'geo_shift'">
          <GeoDistributionInput
            :countries="countries"
            :rows="geoShiftTargetRows"
            @update:rows="geoShiftTargetRows = $event"
          />
          <small
            :class="geoShiftTargetTotal === 100 ? 'text-green-600' : 'p-error'"
          >
            Total: {{ geoShiftTargetTotal }}%
          </small>
          <small v-if="geoShiftTargetUnchanged" class="p-error">
            Target matches the current distribution — change at least one
            country's percentage.
          </small>
          <small class="text-color-secondary">
            Gradually shifts to this split over the chosen number of days,
            updating automatically once a day. You can stop or edit it any time.
          </small>
        </template>
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showAddDialog = false" />
        <Button
          v-if="automationType === 'interval'"
          label="Start"
          :loading="loadingType === 'interval'"
          :disabled="
            targetValue == null ||
            !days ||
            days < 2 ||
            Number(targetValue) === Number(currentValue)
          "
          @click="startChange('interval')"
        />
        <Button
          v-if="automationType === 'geo_shift'"
          label="Start"
          :loading="loadingType === 'geo_shift'"
          :disabled="
            !days ||
            days < 2 ||
            geoShiftTargetTotal !== 100 ||
            geoShiftTargetUnchanged
          "
          @click="startChange('geo_shift')"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showEditDialog"
      :header="`Edit ${typeLabel(editingType)}`"
      modal
      :style="{ width: editingType === 'geo_shift' ? '60rem' : '30rem' }"
    >
      <div class="flex flex-column gap-2 edit-dialog-body">
        <template v-if="editingType === 'interval'">
          <div class="flex gap-3">
            <div style="flex: 1 1 55%">
              <label>Target value</label>
              <InputNumber
                v-model="editTargetValue"
                :min="0"
                :minFractionDigits="0"
                :maxFractionDigits="4"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
            </div>
            <div style="flex: 1 1 45%">
              <label>Total days</label>
              <InputNumber
                v-model="editDays"
                :min="editMinDays"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
              <small
                :class="
                  editDays != null && editDays < editMinDays
                    ? 'p-error'
                    : 'text-color-secondary'
                "
              >
                Must be at least {{ editMinDays }}
              </small>
            </div>
          </div>
        </template>
        <template v-else>
          <GeoDistributionInput
            :countries="countries"
            :rows="editGeoShiftTargetRows"
            @update:rows="editGeoShiftTargetRows = $event"
          />
          <small
            :class="
              editGeoShiftTargetTotal === 100 ? 'text-green-600' : 'p-error'
            "
          >
            Total: {{ editGeoShiftTargetTotal }}%
          </small>
          <div class="flex justify-content-center">
            <div style="width: 13.5rem">
              <label>Total days</label>
              <InputNumber
                v-model="editDays"
                :min="editMinDays"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
              <small
                :class="
                  editDays != null && editDays < editMinDays
                    ? 'p-error'
                    : 'text-color-secondary'
                "
              >
                Must be at least {{ editMinDays }}
              </small>
            </div>
          </div>
        </template>
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showEditDialog = false" />
        <Button
          label="Save"
          :loading="loadingType === editingType"
          :disabled="!isEditValid"
          @click="saveEdit"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import moment from "moment";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Dialog from "primevue/dialog";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { API_BASE_URL } from "@/consts";
import GeoDistributionInput from "./GeoDistributionInput.vue";

// eslint-disable-next-line no-undef
const props = defineProps({
  campaignId: { type: String, required: true },
  currentValue: { type: Number, default: 0 },
  countries: { type: Array, default: () => [] },
  customGeosDistribution: { type: Array, default: null },
  disableIntervalAutomation: { type: Boolean, default: false },
});

const TYPE_LABELS = {
  interval: "Interval Automation",
  geo_shift: "Shift Countries",
};
const typeLabel = (type) => TYPE_LABELS[type] || type;

const toast = useToast();
const confirm = useConfirm();
const token = localStorage.getItem("podcastAdminToken");
const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

const loading = ref(true);
const loadingType = ref(null);
const changes = ref({
  interval: { change: null, logs: [] },
  geo_shift: { change: null, logs: [] },
});

const showAddDialog = ref(false);
const automationType = ref(null);
const targetValue = ref(null);
const days = ref(7);
const geoShiftTargetRows = ref([]);

const showEditDialog = ref(false);
const editingType = ref(null);
const editTargetValue = ref(null);
const editDays = ref(null);
const editGeoShiftTargetRows = ref([]);

const baseUrl = `${API_BASE_URL}/campaigns/${props.campaignId}/scheduled-change`;

const hasCustomGeoDistribution = computed(
  () => props.customGeosDistribution?.length > 0
);

const isRunning = (type) => changes.value[type]?.change?.status === "active";

const runningTypes = computed(() =>
  ["interval", "geo_shift"].filter((type) => isRunning(type))
);

const addableTypeOptions = computed(() => {
  const options = [];
  if (!isRunning("interval") && !props.disableIntervalAutomation)
    options.push({ label: "Interval", value: "interval" });
  if (hasCustomGeoDistribution.value && !isRunning("geo_shift")) {
    options.push({ label: "Shift Countries", value: "geo_shift" });
  }
  return options;
});

const isEditValid = computed(() => {
  if (editDays.value == null || editDays.value < editMinDays.value)
    return false;
  if (editingType.value === "interval") return editTargetValue.value != null;
  return editGeoShiftTargetTotal.value === 100;
});

const sumPct = (rows) => rows.reduce((sum, row) => sum + (row.pct || 0), 0);

const geoShiftTargetTotal = computed(() => sumPct(geoShiftTargetRows.value));
const editGeoShiftTargetTotal = computed(() =>
  sumPct(editGeoShiftTargetRows.value)
);

const geoDistributionKey = (distribution) =>
  [...(distribution || [])]
    .sort((a, b) => a.geo.localeCompare(b.geo))
    .map((row) => `${row.geo}:${row.pct}`)
    .join(",");

const geoShiftTargetUnchanged = computed(
  () =>
    geoDistributionKey(geoShiftTargetRows.value) ===
    geoDistributionKey(props.customGeosDistribution)
);

const countryName = (geo) =>
  props.countries.find((c) => c.geo === geo)?.name || geo?.toUpperCase();

const formatValue = (v) => (v == null ? "-" : Number(v).toFixed(2));
const formatDate = (d) => moment(d).format("MMM D, HH:mm");

const geoShiftRows = (change) => {
  if (!change) return [];
  const start = JSON.parse(change.start_value);
  const current = JSON.parse(change.current_value);
  const target = JSON.parse(change.target_value);
  return start.map((row) => ({
    geo: row.geo,
    start: row.pct,
    current: current.find((r) => r.geo === row.geo)?.pct,
    target: target.find((r) => r.geo === row.geo)?.pct,
  }));
};

const elapsedDays = (type) => {
  const change = changes.value[type].change;
  if (!change) return 0;
  const startDay = moment.utc(change.start_date).startOf("day");
  const today = moment.utc().startOf("day");
  return today.diff(startDay, "days");
};

const daysLeft = (type) => {
  const change = changes.value[type].change;
  if (!change) return 0;
  return Math.max(0, change.days - elapsedDays(type));
};

const editMinDays = computed(() => elapsedDays(editingType.value) + 1);

const fetchType = async (type) => {
  const response = await axios.get(`${baseUrl}?type=${type}`, authHeaders);
  changes.value[type] = {
    change: response.data.data.change,
    logs: response.data.data.logs || [],
  };
};

const fetchState = async () => {
  loading.value = true;
  try {
    await fetchType("interval");
    await fetchType("geo_shift");
  } catch (error) {
    console.error("Error fetching scheduled change:", error);
  }
  loading.value = false;
};

const buildGeoShiftRows = () =>
  (props.customGeosDistribution || []).map((row) => ({ ...row }));

const openAddDialog = () => {
  automationType.value = null;
  targetValue.value = props.currentValue;
  days.value = 7;
  geoShiftTargetRows.value = buildGeoShiftRows();
  showAddDialog.value = true;
};

const startChange = async (type) => {
  loadingType.value = type;
  try {
    await axios.post(
      baseUrl,
      {
        changeType: type,
        targetValue:
          type === "geo_shift" ? geoShiftTargetRows.value : targetValue.value,
        days: days.value,
      },
      authHeaders
    );
    showAddDialog.value = false;
    toast.add({
      severity: "success",
      summary: "Started",
      detail: `${typeLabel(type)} started`,
      life: 3000,
    });
    await fetchState();
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Could not start automation",
      life: 4000,
    });
  }
  loadingType.value = null;
};

const stopChange = async (type) => {
  loadingType.value = type;
  try {
    await axios.patch(`${baseUrl}/stop`, { changeType: type }, authHeaders);
    await fetchState();
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Could not stop automation",
      life: 4000,
    });
  }
  loadingType.value = null;
};

const confirmStop = (type) => {
  const message =
    type === "interval"
      ? `Freeze the volume at ${formatValue(
          changes.value[type].change.current_value
        )}? No more automatic changes will happen after this.`
      : "Freeze the current country split? No more automatic changes will happen after this.";

  confirm.require({
    message,
    header: "Stop Confirmation",
    icon: "pi pi-info-circle",
    acceptClass: "p-button-danger",
    accept: () => stopChange(type),
  });
};

const openEditDialog = (type) => {
  editingType.value = type;
  const change = changes.value[type].change;
  if (type === "interval") {
    editTargetValue.value = Number(change.target_value);
  } else {
    editGeoShiftTargetRows.value = JSON.parse(change.target_value);
  }
  editDays.value = change.days;
  showEditDialog.value = true;
};

const saveEdit = async () => {
  loadingType.value = editingType.value;
  try {
    await axios.patch(
      `${baseUrl}/edit`,
      {
        changeType: editingType.value,
        targetValue:
          editingType.value === "geo_shift"
            ? editGeoShiftTargetRows.value
            : editTargetValue.value,
        days: editDays.value,
      },
      authHeaders
    );
    showEditDialog.value = false;
    toast.add({
      severity: "success",
      summary: "Updated",
      detail: `${typeLabel(editingType.value)} updated`,
      life: 3000,
    });
    await fetchState();
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Could not update automation",
      life: 4000,
    });
  }
  loadingType.value = null;
};

onMounted(fetchState);
</script>

<style scoped>
.automation-card {
  border: 1px solid var(--surface-border);
  border-left: 4px solid var(--primary-color);
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.automation-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.automation-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color);
  flex: none;
}

.automation-title {
  font-weight: 600;
}

.edit-dialog-body {
  margin-bottom: -1rem;
}
</style>
